import type { FC } from "react"
import { Navigate, Outlet, useLocation } from "react-router"
import { LoadingSpinner } from "@/components/LoadingSpinner"
import { TenantHeader } from "@/components/tenant/tenant-header"
import { TenantAccessProvider, useTenantAccess } from "./lib/tenant-access"
import { useTenantOnboardingStatus } from "./lib/use-tenant-onboarding-status"
import { useCurrentUser } from "./lib/useCurrentUser"

const AuthenticatedTenantShell = () => {
  const { isResolving } = useTenantAccess()

  if (isResolving) {
    return (
      <main className="h-screen flex items-center justify-center">
        <LoadingSpinner />
      </main>
    )
  }

  return (
    <>
      <TenantHeader />
      <main className="mx-auto w-full max-w-screen-xl pb-6 md:pt-6 md:px-4">
        <Outlet />
      </main>
    </>
  )
}

export const AuthenticatedLayout: FC = () => {
  const currentUser = useCurrentUser()
  const location = useLocation()
  const { isResolving, needsOnboarding } = useTenantOnboardingStatus()

  if (!currentUser) {
    return <Navigate to="/login" replace state={{ from: location }} />
  }
  if (
    currentUser.providerData.some((provider) => provider.providerId === "password") &&
    !currentUser.emailVerified
  ) {
    return <Navigate to="/signup/email-not-verified" replace state={{ from: location }} />
  }
  if (isResolving) {
    return (
      <main className="h-screen flex items-center justify-center">
        <LoadingSpinner />
      </main>
    )
  }
  if (needsOnboarding) {
    return <Navigate to="/signup/info" replace state={{ from: location }} />
  }

  return (
    <TenantAccessProvider>
      <AuthenticatedTenantShell />
    </TenantAccessProvider>
  )
}
